import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type { FormikProps } from 'formik';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TextAnalysisResponse } from '../../../../components/Dialogs/TextAnalysisResponse';
import ModerationBanner, {
  type ModerationMeta,
} from '../../../../components/Post/ModerationBanner';
import { RichEditorBody } from '../../../../components/TextEditor/RichEditorBody';
import ButtonComponent from '../../../../components/UI/Button/Button.component';
import type { SetFieldValue } from '../../../../constants/textEditor.constants';
import { useAuth } from '../../../../context/AuthContext';
import { REPUBLISH_POST_MUTATION } from '../../../../graphql/resolvers/postMutations/republishPost.gql';
import { SAVE_REWRITE_MUTATION } from '../../../../graphql/resolvers/postMutations/saveRewrite.gql';
import { UPDATE_POST_STORY_ANALYSIS_MUTATION } from '../../../../graphql/resolvers/postMutations/updatePostStoryAnalysis.gql';
import { GET_POST_BY_ID_QUERY } from '../../../../graphql/resolvers/postQueries/getPostById.gql';
import { removeTypenames } from '../../../../helpers/removeTypenameFromPayload';
import { extractTextFromBlocks } from '../../../../helpers/textBlockExtractor';
import { triggerAnalysis } from '../../../../helpers/triggerAnalysis';
import type { AnalysisResponse } from '../../../../interfaces/analysisResponse';
import type { SentimentResponse } from '../../../../interfaces/sentimentResponse';
import type { TextBlock } from '../../../../interfaces/textEditor.interface';
import { deriveState } from './RewriteStateMachine';
import './edit-rewrite.scss';

const cleanBlock = (block: unknown): TextBlock =>
  removeTypenames(block) as TextBlock;

interface AnalysisModalState {
  open: boolean;
  analysis?: AnalysisResponse;
  sentiment?: SentimentResponse | null;
}

export default function EditRewrite() {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const { user } = useAuth();
  const { data, loading, error, refetch } = useQuery(GET_POST_BY_ID_QUERY, {
    variables: { postId: postId ?? '' },
    skip: !postId,
    fetchPolicy: 'network-only',
  });

  const [saveRewrite, { loading: saving }] = useMutation(SAVE_REWRITE_MUTATION);
  const [updateAnalysis, { loading: analyzing }] = useMutation(
    UPDATE_POST_STORY_ANALYSIS_MUTATION,
    {
      update(cache, { data }) {
        const updated = data?.updatePostStoryAnalysis;
        if (!updated?.id) return;
        const cacheId = cache.identify({ __typename: 'Post', id: updated.id });
        if (!cacheId) return;
        cache.modify({
          id: cacheId,
          fields: {
            status: () => updated.status,
            savedSinceModeration: () => updated.savedSinceModeration,
            analyzedSinceLastSave: () => updated.analyzedSinceLastSave,
            updatedAt: () => updated.updatedAt,
          },
        });
      },
    },
  );
  const [republishPost, { loading: republishing }] = useMutation(
    REPUBLISH_POST_MUTATION,
  );

  // Local editing toggle: true when the user clicked "Edit again" after a save.
  const [isEditingToggle, setIsEditingToggle] = useState(false);
  // Draft blocks: initialized once from post data, then owned locally.
  const [draftBlocks, setDraftBlocks] = useState<TextBlock[]>([]);
  // Snapshot of blocks when entering preview, used to restore on Cancel.
  const [savedBlocksSnapshot, setSavedBlocksSnapshot] = useState<TextBlock[]>(
    [],
  );

  const [analysisModal, setAnalysisModal] = useState<AnalysisModalState>({
    open: false,
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const [analyzingBackdrop, setAnalyzingBackdrop] = useState(false);

  // RichEditorBody menu-anchor state — mirrors TextEditor/index.tsx wiring.
  const fileInputRef = useRef<null>(null);
  const formikRef = useRef<FormikProps<any> | null>(null);
  const [menuAnchors, setMenuAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [headingMenuAnchors, setHeadingMenuAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  // Hydrate draftBlocks once per post when post data first arrives.
  // TRIL-155: identity-keyed on post id (not a one-shot boolean) so that if the
  // EditRewrite instance is reused across post ids — React Router does this when
  // navigating between two /post/:postId/edit-rewrite URLs without a remount —
  // the new post's blocks hydrate instead of keeping the previous post's stale
  // blocks (the TRIL-141 mount-once-ref bug class).
  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    const existing = data?.getPostById?.textBlocks;
    const postId = data?.getPostById?.id ?? null;
    if (existing && Array.isArray(existing) && hydratedRef.current !== postId) {
      hydratedRef.current = postId;
      setDraftBlocks(existing.map(cleanBlock));
    }
    // Intentionally hydrate only on post ID change, not on every `data` change.
    // (useExhaustiveDependencies is a warning here; not suppressed — biome's
    // suppression-unused detection flip-flops on this effect under its cache,
    // which produced a flaky suppressions/unused error. TRIL-160.)
  }, [data?.getPostById?.id]);

  if (loading && !data) return <div className="EditRewrite">Loading…</div>;
  if (error)
    return (
      <div className="EditRewrite EditRewrite--error">Failed to load post.</div>
    );

  const post = data?.getPostById;
  if (!post) {
    navigate(`/post/${postId}`, { replace: true });
    return null;
  }

  const viewerId = user?.id ?? null;
  if (post.authorId !== viewerId) {
    navigate(`/post/${postId}`, { replace: true });
    return null;
  }

  // TRIL-219: only `hidden_pending_rewrite` posts can be rewritten. The server-side
  // `saveRewrite` mutation enforces the same rule (returns `Forbidden` otherwise),
  // but without this client-side gate the editor UI is fully interactive for posts
  // where every save attempt will fail. This re-adds a client-side gate that was
  // intentionally removed during Plan D (2026-05-06) when the server-side check
  // was deemed sufficient; demo smoke surfaced the UX gap. Wrong-status visits
  // now redirect to the post detail page, matching the existence + authorship gates.
  if (post.status !== 'hidden_pending_rewrite') {
    navigate(`/post/${postId}`, { replace: true });
    return null;
  }

  const uiState = deriveState(
    {
      savedSinceModeration: post.savedSinceModeration ?? undefined,
      analyzedSinceLastSave: post.analyzedSinceLastSave ?? undefined,
    },
    isEditingToggle,
  );
  const isEditing = uiState === 'EDITING';
  const isSavedPreview = uiState === 'SAVED_PREVIEW';
  const isAnalyzedPreview = uiState === 'ANALYZED_PREVIEW';

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleSave = async (payload?: { textBlocks?: TextBlock[] }) => {
    setActionError(null);
    const blocks = payload?.textBlocks ?? draftBlocks;
    try {
      await saveRewrite({
        variables: {
          postId: postId ?? '',
          textBlocks: blocks.map(cleanBlock),
        },
      });
      setDraftBlocks(blocks);
      setSavedBlocksSnapshot([...blocks]);
      setIsEditingToggle(false);
      await refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const handlePageSave = async () => {
    const liveBlocks = formikRef.current?.values?.blocks ?? draftBlocks;
    await handleSave({ textBlocks: liveBlocks });
  };

  const handleEditAgain = () => {
    setIsEditingToggle(true);
  };

  const handleCancel = () => {
    if (isEditingToggle) {
      setDraftBlocks(savedBlocksSnapshot);
      setIsEditingToggle(false);
    } else {
      navigate(`/post/${postId}`);
    }
  };

  const handleAnalyze = async () => {
    setActionError(null);
    setAnalyzingBackdrop(true);
    try {
      const storyText = extractTextFromBlocks(draftBlocks);
      const { analysis, sentiment } = await triggerAnalysis({
        storyText,
        articleSources: [],
        verifySources: false,
      });
      setAnalysisModal({ open: true, analysis, sentiment });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setAnalyzingBackdrop(false);
    }
  };

  const handleAcceptAnalysis = async () => {
    setActionError(null);
    try {
      await updateAnalysis({
        variables: {
          postId: postId ?? '',
          analysis: analysisModal.analysis ?? null,
        },
      });
      setAnalysisModal({ open: false });
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to save analysis',
      );
    }
  };

  const handleRepublish = async () => {
    setActionError(null);
    try {
      await republishPost({ variables: { postId: postId ?? '' } });

      // Evict the cached Post entity so the network-only query below is
      // guaranteed fresh and doesn't merge into stale cache fields.
      const cacheId = apolloClient.cache.identify({
        __typename: 'Post',
        id: postId,
      });
      if (cacheId) {
        apolloClient.cache.evict({ id: cacheId });
        apolloClient.cache.gc();
      }

      // Pull the fresh full post (textBlocks + score + moderation:null)
      // so the Apollo cache reflects the post-republish state before the
      // detail page mounts. Without this, the page reads stale entries on
      // first render and only updates after a manual nav-away and back.
      // Errors are swallowed: the next mount path covers the edge case
      // where the query fails.
      try {
        const freshResult = await apolloClient.query({
          query: GET_POST_BY_ID_QUERY,
          variables: { postId: postId ?? '' },
          fetchPolicy: 'network-only',
        });
        const freshPost = freshResult.data?.getPostById;
        if (freshPost && freshPost.id) {
          // TRIL-177 / TRIL-194: the recommendation feed renders from the
          // Apollo cache. A rewrite must reach the cached getUserFeed list.
          // The post is normalized as Post:{id}; if it's already in the
          // list, normalization refreshes its fields — only prepend its
          // ref when it's absent (e.g. a post returning from
          // hidden_pending_rewrite to published). The modifier runs once
          // per cached getUserFeed keyArgs bucket (every feedType/topic),
          // which is what we want — the republished post belongs in each.
          apolloClient.cache.modify({
            fields: {
              getUserFeed(existing: any, { toReference, readField }: any) {
                if (!existing?.posts) return existing;
                const ref = toReference({
                  __typename: 'Post',
                  id: (freshPost as { id?: string }).id,
                });
                const id = (freshPost as { id?: string }).id;
                const present = existing.posts.some(
                  (p: any) => readField('id', p) === id,
                );
                if (present || !ref) return existing;
                return { ...existing, posts: [ref, ...existing.posts] };
              },
            },
          });
          // Eventual /posts page refresh next time the author visits their
          // profile: evict the getUserPosts field; watchQuery's cache-and-network
          // refetches on next mount. Replaces the legacy REFRESH_POSTS Redux flag.
          apolloClient.cache.evict({ fieldName: 'getUserPosts' });
          apolloClient.cache.gc();
        }
      } catch (queryErr) {
        console.warn(
          '[EditRewrite] post-republish refetch failed (non-fatal)',
          queryErr,
        );
      }

      // refreshAfterSave kept as defense-in-depth in case the query above
      // failed: the post detail page detects the signal and re-fires its
      // own getPostById on mount.
      navigate(`/post/${postId}`, { state: { refreshAfterSave: Date.now() } });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Republish failed');
    }
  };

  // ─── RichEditorBody menu handlers ─────────────────────────────────────────

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => {
    setMenuAnchors((prev) => ({ ...prev, [blockId]: event.currentTarget }));
  };

  const handleOpenHeadingMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => {
    setHeadingMenuAnchors((prev) => ({
      ...prev,
      [blockId]: event.currentTarget,
    }));
  };

  const handleCloseHeadingMenu = (blockId: string) => {
    setHeadingMenuAnchors((prev) => ({ ...prev, [blockId]: null }));
  };

  const handleHeadingLevelChange = (
    index: number,
    level: number,
    setFieldValue: SetFieldValue,
  ) => {
    setFieldValue(`blocks.${index}.data.level`, level);
  };

  const handleMenuClose = (blockId: string) => {
    setMenuAnchors((prev) => ({ ...prev, [blockId]: null }));
  };

  // ─── Busy flag ────────────────────────────────────────────────────────────

  const isBusy = saving || analyzing || analyzingBackdrop || republishing;

  // ─── Action bar button states ─────────────────────────────────────────────

  // Left side: Cancel (always) or Edit-again (preview states)
  // Right side: Save (editing) | Analyze | Republish

  const showSave = isEditing;
  const showEditAgain = isSavedPreview || isAnalyzedPreview;
  // Analyze only shown in SAVED_PREVIEW — hidden after analysis is accepted (ANALYZED_PREVIEW)
  // and hidden while editing (EDITING).
  const showAnalyze = isSavedPreview;

  const saveDisabled = isBusy || draftBlocks.length === 0;
  const republishDisabled = isBusy || !isAnalyzedPreview;

  // ─── Render ───────────────────────────────────────────────────────────────

  const actionBar = (position: 'top' | 'bottom') => (
    <div
      className={`EditRewrite__ActionBar EditRewrite__ActionBar--${position}`}
    >
      <div className="EditRewrite__ActionBar__Left">
        <ButtonComponent
          variant="outline"
          size="sm"
          onClick={handleCancel}
          disabled={isBusy}
        >
          Cancel
        </ButtonComponent>
        {showEditAgain && (
          <ButtonComponent
            variant="outline"
            size="sm"
            onClick={handleEditAgain}
            disabled={isBusy}
          >
            Edit again
          </ButtonComponent>
        )}
      </div>
      <div className="EditRewrite__ActionBar__Right">
        {showSave && (
          <ButtonComponent
            variant="primary"
            size="sm"
            onClick={handlePageSave}
            disabled={saveDisabled}
            loading={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </ButtonComponent>
        )}
        {/* Analyze only in the top bar, and only in SAVED_PREVIEW state */}
        {position === 'top' && showAnalyze && (
          <ButtonComponent
            variant="saphir"
            size="sm"
            onClick={handleAnalyze}
            disabled={isBusy}
            loading={analyzing}
          >
            {analyzing ? 'Analyzing…' : 'Analyze'}
          </ButtonComponent>
        )}
        <ButtonComponent
          variant="primary"
          size="sm"
          onClick={handleRepublish}
          disabled={republishDisabled}
          loading={republishing}
        >
          {republishing ? 'Republishing…' : 'Republish'}
        </ButtonComponent>
      </div>
    </div>
  );

  return (
    <div className="EditRewrite">
      <div className="EditRewrite__Header">
        <h1>Edit & Republish</h1>
        {/* TRIL-281: hide once rewritten + reanalyzed (ready to republish) so
            the chip doesn't contradict the active Republish action. */}
        {!isAnalyzedPreview && (
          <span className="EditRewrite__Header__Chip">PENDING REWRITE</span>
        )}
      </div>

      {actionBar('top')}

      <div className="EditRewrite__Body">
        {actionError && <div className="EditRewrite__Error">{actionError}</div>}

        {isEditing ? (
          draftBlocks.length > 0 ? (
            <RichEditorBody
              username={user?.username ?? ''}
              userId={viewerId ?? ''}
              initialBlocks={draftBlocks}
              fileInputRef={fileInputRef}
              menuAnchors={menuAnchors}
              headingMenuAnchors={headingMenuAnchors}
              handleMenuClick={handleMenuClick}
              handleOpenHeadingMenu={handleOpenHeadingMenu}
              handleCloseHeadingMenu={handleCloseHeadingMenu}
              handleHeadingLevelChange={handleHeadingLevelChange}
              handleMenuClose={handleMenuClose}
              handleSave={handleSave}
              hideSubmitButton={true}
              formikRef={formikRef}
            />
          ) : null
        ) : (
          <div className="EditRewrite__Preview">
            {/* TRIL-281: `post.moderation` stays non-null until republish, so
                also gate on !isAnalyzedPreview to drop the "Please rewrite this
                post" banner once the post is ready to republish. */}
            {post.moderation && !isAnalyzedPreview && (
              <ModerationBanner
                meta={post.moderation as unknown as ModerationMeta}
              />
            )}
            {(post.textBlocks as unknown as TextBlock[] | undefined)?.map(
              (block: TextBlock, index: number) => (
                <div
                  key={block.id ?? index}
                  className={`EditRewrite__Preview__Block EditRewrite__Preview__Block--${block.type?.toLowerCase()}`}
                >
                  {block.type === 'Heading' && (
                    <p className="EditRewrite__Preview__Heading">
                      {(block.data?.header as string | undefined) ?? ''}
                    </p>
                  )}
                  {block.type === 'Paragraph' && (
                    <p>{(block.data?.paragraph as string | undefined) ?? ''}</p>
                  )}
                  {block.type !== 'Heading' && block.type !== 'Paragraph' && (
                    <p className="EditRewrite__Preview__Other">
                      [{block.type} block]
                    </p>
                  )}
                </div>
              ),
            )}
          </div>
        )}
      </div>

      {actionBar('bottom')}

      <TextAnalysisResponse
        isModalOpen={analysisModal.open}
        hasStoryAnalysis={false}
        isReAnalyze={false}
        setShowAnalysisModal={(open) =>
          setAnalysisModal((prev) => ({ ...prev, open: open as boolean }))
        }
        handleClickClose={async () => setAnalysisModal({ open: false })}
        handleAcceptAnalysis={handleAcceptAnalysis}
        analysisResult={analysisModal.analysis as AnalysisResponse}
        draftTextSentiment={analysisModal.sentiment as SentimentResponse}
        handleEdit={async () => {
          /* not used in EditRewrite flow */
        }}
        draftStoryAnalyzerPluginResults={null}
        userId={user?.id}
      />

      {analyzingBackdrop && (
        <div
          className="EditRewrite__Backdrop"
          role="status"
          aria-label="Analyzing…"
        >
          <span className="EditRewrite__Backdrop__Spinner" aria-hidden="true" />
        </div>
      )}
    </div>
  );
}
